"use client";
import type { RawProductAttribute } from "@graphql/generated/types";
import { useIsHydrated } from "@hooks/useIsHydrated";
import type { SizeLocales } from "@lib/constants/filtersConstants";
import { ModalTypes } from "@lib/enums/ModalTypes";
import { useFeatureFlag } from "@lib/feature-flags/FeatureFlagsContext";
import { Attributes } from "@lib/enums/ProductAttributes";
import { getProductAttributeValue } from "@lib/utils/attributesUtils";
import { hasNotifySizeAvailability } from "@lib/utils/notificationFeature";
import type { NotShoeSizeSelectable, ShoeSizeSelectable } from "@lib/utils/pdp/pdpShoeSizes";
import { getVariantSize } from "@lib/utils/pdp/pdpShoeSizes";
import { getVariantAttribute, getAvailability } from "@lib/utils/productUtility";
import { useStore } from "@store/index";
import { Text } from "@ecco/ui/text";
import { Skeleton } from "@ecco/ui/skeleton";
import { Button } from "@ecco/ui/button";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import { ECCOIcon } from "@ecco/ui/ecco-icon";
import { isNumber, isString } from "lodash-es";
import dynamic from "next/dynamic";
import { useSearchParams, useParams } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import { useProductSizeInfo } from "./hooks/useProductSizeInfo";
import { useProductSizeHandlers } from "./hooks/useProductSizeHandlers";
import type { ProductVariant } from "@lib/product/types";
import type { RecommendationResult } from "@lib/algolia/types";
import {
    sizeWrapperStyles,
    notifyBellStylesBox,
    sizeGridCellButtonStyles,
    sizeSystemToggleButtonStyles,
} from "./ProductBuyCardSizes.styles";
import { RUM_ACTION_NAME } from "@lib/monitoring/rumActionNames";

const SizeGuide = dynamic(
    () => import("@components/ui/size-guide-modal/SizeGuidePdp").then((mod) => mod.SizeGuide),
    {
        ssr: false,
    }
);

const NotifySizeAvailability = dynamic(
    () => import("@components/commerce/product/notify-size-availability")
);

const SizeWizard = dynamic(() => import("@components/size-wizard/SizeWizard"), { ssr: false });

interface ProductBuyCardSizesProps {
    variants: ProductVariant[];
    attributes: RawProductAttribute[];
    productName: string;
    isStickyModal?: boolean;
    /** When set, overrides the default bottom margin on the root wrapper (e.g. PDP buy card). */
    marginBottom?: number;
    isHighlighted?: boolean;
    recommendations?: RecommendationResult[];
    fitFeedback?: {
        selectedSize: number;
    };
}

export const productBuyCardSizesWrapper = "product-buy-card-sizes-wrapper";
export const TEST_ID_SHOE_SIZE_BUTTON = "shoe-size-button";

const ProductBuyCardSizes = ({
    variants,
    attributes,
    isStickyModal,
    productName,
    marginBottom,
    isHighlighted,
    recommendations = [],
    fitFeedback,
}: ProductBuyCardSizesProps) => {
    const activeProduct = useStore((state) => state.data.pdpState.activeProduct);
    const selectedVariant = activeProduct?.variant;
    const searchParams = useSearchParams();
    const params = useParams();
    const size = searchParams.get("size");
    const system = searchParams.get("system");
    const unavailableButtonRef = useRef(null);
    const globalShoeSizeConvention = useStore((state) => state.data.me?.globalShoeSizeConvention);
    const setGlobalShoeSizeConvention = useStore((state) => state.setGlobalShoeSizeConvention);
    const isHydrated = useIsHydrated();
    const isSizeWizardEnabled = useFeatureFlag("isSizeWizardEnabled");
    const [showNotifyModal, setShowNotifyModal] = useState(false);
    const sizeRef = useRef(null);

    const {
        microcopies,
        gender,
        targetGroup,
        materialGroup,
        isProbablyOneSize,
        hasAlternativeSize,
        isFootwearProduct,
        sizeLabels,
        availableConventions,
        shoeSizeSystems,
        currentlyUsedUnit,
        selectedShoeSizeSystem,
    } = useProductSizeInfo({
        variants,
        attributes,
        globalShoeSizeConvention,
    });

    const { handleSelectVariant, handleSizeSystemClick, handleSizeClick } = useProductSizeHandlers({
        variants,
        currentlyUsedUnit,
        size,
        colorCode: activeProduct?.colorCode ?? (params.colorCode as string),
        setShowNotifyModal,
    });

    useEffect(() => {
        if (isHighlighted) {
            sizeRef?.current?.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
        }
    }, [isHighlighted]);

    const selectedVariantAvailability = useMemo(() => {
        if (!selectedVariant) return null;
        return getAvailability(selectedVariant.availability);
    }, [selectedVariant]);

    useEffect(() => {
        if (selectedVariant) return;
        if (size) {
            const isExistingSize = sizeLabels.find((it) => it.sizeData.toString() === size);

            if (isExistingSize) {
                handleSelectVariant({ isAvailable: isExistingSize.available, size });
            }
        }
        if (system && availableConventions.some((it) => it === system)) {
            setGlobalShoeSizeConvention(system as SizeLocales);
        }
    }, [
        size,
        system,
        sizeLabels,
        handleSelectVariant,
        setGlobalShoeSizeConvention,
        availableConventions,
    ]);

    useEffect(() => {
        if (!isProbablyOneSize) {
            return;
        }
        const singleSize = getVariantSize(variants);
        if (!singleSize) {
            return;
        }
        void handleSelectVariant({ size: singleSize, isAvailable: true });
    }, [isProbablyOneSize, variants, handleSelectVariant]);

    if (isProbablyOneSize) {
        return (
            <Box
                mb={marginBottom !== undefined ? marginBottom : 4}
                data-testid={productBuyCardSizesWrapper}
            />
        );
    }

    const rootMb = marginBottom !== undefined ? marginBottom : isStickyModal ? 0 : 8;

    return (
        <Box
            mb={rootMb}
            data-testid={productBuyCardSizesWrapper}
            data-vwo-element="product-buy-card-sizes"
        >
            <Flex
                gap={1}
                mb={2}
                flexWrap="wrap"
                align={isStickyModal ? "flex-start" : "center"}
                justifyContent={"space-between"}
            >
                <Flex
                    gap={isStickyModal ? 4 : 2}
                    align={"center"}
                    flexDirection={isStickyModal ? "column" : "row"}
                    alignItems={isStickyModal ? "flex-start" : "center"}
                >
                    <Text fontWeight={"bold"} size={"medium"}>
                        {microcopies.sizeTitle}
                    </Text>
                    <Flex
                        gap={1.2}
                        flexWrap="wrap"
                        alignItems={"center"}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {!hasAlternativeSize &&
                            shoeSizeSystems.map((item) => {
                                const { key, label, aria } = item;
                                if (!key) {
                                    return null;
                                }
                                const isActive = key === selectedShoeSizeSystem.key;
                                return isHydrated ? (
                                    <Button
                                        key={key}
                                        {...sizeSystemToggleButtonStyles(isActive)}
                                        onClick={() => handleSizeSystemClick(key)}
                                        aria-label={aria}
                                    >
                                        {label || key}
                                    </Button>
                                ) : (
                                    <Skeleton key={key} px={4} w={12} h={6} />
                                );
                            })}
                    </Flex>
                </Flex>
                <Box
                    alignSelf="center"
                    data-vwo-element="size-guide"
                    id={isStickyModal ? undefined : "pdp-size-guide-variant"}
                    onClick={(e) => e.stopPropagation()}
                >
                    <SizeGuide
                        gender={gender}
                        targetGroup={targetGroup}
                        materialGroup={materialGroup}
                        isFootwear={isFootwearProduct}
                        type={ModalTypes.sizeGuide}
                        actionButtonId={isStickyModal ? undefined : "size-guide-action-button"}
                        fitFeedbackSelectedSize={fitFeedback?.selectedSize}
                    />
                </Box>
            </Flex>
            <Box
                display="grid"
                gridTemplateColumns="repeat(4, 1fr)"
                gap={0}
                ref={sizeRef}
                {...sizeWrapperStyles(isHighlighted)}
                flexWrap="wrap"
                borderColor={isHighlighted ? "red.500" : "none"}
                borderStyle={isHighlighted ? "solid" : "none"}
                borderWidth={isHighlighted ? "1px" : "0"}
            >
                {sizeLabels.map((label: ShoeSizeSelectable & NotShoeSizeSelectable) => {
                    const isSelected =
                        selectedVariant &&
                        getVariantAttribute(selectedVariant, Attributes.size) ===
                            label.sizeData.toString();
                    const fallback = hasAlternativeSize ? label?.sizeEU : `${label?.sizeEU} EU`;

                    const selectedConventionLabel =
                        label[`size${selectedShoeSizeSystem.key}`] ||
                        label[`size${selectedShoeSizeSystem.label}`];

                    const productLabel =
                        isString(selectedConventionLabel) || isNumber(selectedConventionLabel)
                            ? selectedConventionLabel
                            : fallback;

                    if (!isHydrated) {
                        return (
                            <Box key={`size-${label.sizeData}`} w="full" minW={0}>
                                <Skeleton w="full" h={10} />
                            </Box>
                        );
                    }
                    const notifyBell = !label.available && hasNotifySizeAvailability && (
                        <Box as="span" aria-hidden="true" {...notifyBellStylesBox}>
                            <ECCOIcon size="xs" name="bell" />
                        </Box>
                    );

                    return isFootwearProduct ? (
                        <Box key={`size-${label.sizeData}`} position="relative" width="100%">
                            <Button
                                {...sizeGridCellButtonStyles({
                                    isSelected,
                                    isAvailable: label.available,
                                    hasNotifySizeAvailability,
                                    isFootwear: true,
                                })}
                                onClick={() =>
                                    handleSizeClick(label.available, label.sizeData.toString())
                                }
                                ref={unavailableButtonRef}
                                data-testid={TEST_ID_SHOE_SIZE_BUTTON}
                                aria-label={
                                    !label.available && hasNotifySizeAvailability
                                        ? `Notify me when size ${productLabel} is available`
                                        : undefined
                                }
                                data-dd-action-name={RUM_ACTION_NAME.SELECT_SIZE}
                            >
                                <Text
                                    as="span"
                                    fontWeight={isSelected ? 700 : 400}
                                    textDecoration={label.available ? "none" : "line-through"}
                                    textDecorationThickness={label.available ? undefined : "0.5px"}
                                    whiteSpace="nowrap"
                                >
                                    {productLabel}
                                </Text>
                            </Button>
                            {notifyBell}
                        </Box>
                    ) : (
                        <Box key={`size-${label.sizeData}`} position="relative" width="100%">
                            <Button
                                {...sizeGridCellButtonStyles({
                                    isSelected,
                                    isAvailable: label.available,
                                    hasNotifySizeAvailability,
                                    isFootwear: false,
                                })}
                                ref={unavailableButtonRef}
                                data-testid={TEST_ID_SHOE_SIZE_BUTTON}
                                onClick={() =>
                                    handleSizeClick(label.available, label.sizeData.toString())
                                }
                                aria-label={
                                    !label.available && hasNotifySizeAvailability
                                        ? `Notify me when size ${productLabel} is available`
                                        : undefined
                                }
                                data-dd-action-name={RUM_ACTION_NAME.SELECT_SIZE}
                            >
                                <Text
                                    as="span"
                                    fontWeight={isSelected ? 700 : 400}
                                    textDecoration={label.available ? "none" : "line-through"}
                                    textDecorationThickness={label.available ? undefined : "0.5px"}
                                    whiteSpace="nowrap"
                                >
                                    {productLabel}
                                </Text>
                            </Button>
                            {notifyBell}
                        </Box>
                    );
                })}
            </Box>
            {selectedVariantAvailability &&
                selectedVariantAvailability.availableQuantity === 1 &&
                selectedVariant && (
                    <Box mt={3} py="7px" pl={0} display="flex" alignItems="center" gap={2}>
                        <ECCOIcon name="info" size="sm" color="black" />
                        <Text fontSize="mobileBodyTextNormal" color="black" fontWeight="normal">
                            {microcopies.onlyOneItemLeft}
                        </Text>
                    </Box>
                )}
            {isSizeWizardEnabled && fitFeedback && (
                <Box
                    mt={4}
                    mb={6}
                    data-vwo-element="size-wizard"
                    id={isStickyModal ? undefined : "pdp-size-wizard-variant"}
                >
                    <SizeWizard
                        selectedSize={fitFeedback.selectedSize}
                        actionButtonId={isStickyModal ? undefined : "size-wizard-action-button"}
                        sizeGuideOpenProps={{
                            gender,
                            targetGroup,
                            materialGroup,
                            isFootwear: isFootwearProduct,
                            isSocks: materialGroup?.toLowerCase() === "socks",
                            forKids: targetGroup?.toLowerCase() === "kids",
                        }}
                    />
                </Box>
            )}
            {hasNotifySizeAvailability && showNotifyModal && selectedVariant && (
                <NotifySizeAvailability
                    isOpen={showNotifyModal}
                    onClose={() => setShowNotifyModal(false)}
                    selectedSize={
                        getProductAttributeValue(
                            selectedVariant.attributesRaw,
                            Attributes.size
                        )?.toString() ?? ""
                    }
                    selectedProductSku={selectedVariant.sku}
                    productName={productName || ""}
                    recommendations={recommendations}
                />
            )}
        </Box>
    );
};

export default ProductBuyCardSizes;
